import { InfoPage } from '@/components/info-page';
import { getCoachSessionPaymentAsCoach } from '@/lib/api-calls';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { airColumnHelper } from '@/lib/tables';
import {
  CoachPaymentSessionDetails,
  coachPaymentSessionDetailsSchema,
  PayCycleDTO,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';
import { EntityTable } from '@/components/table/entity-table';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { formatInstant, formatPlainDate } from '@airreading/common';
import { formatCurrency } from '@/lib/common-utils';
import { SessionCompletionStatusCell } from '@/routes/_layout/sessions/-components/session-completion-status-cell';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { usePaginatedFilters } from '@/lib/filtering';
import { useTimeZone } from '@/auth/useTimeZone';

export const Route = createFileRoute('/_layout/payments/teaching/$payCycleId/')({
  component: TeachSessionPaymentsDetails,
  validateSearch: paginationRequestSchema,
});

const columnHelper = airColumnHelper<CoachPaymentSessionDetails>(coachPaymentSessionDetailsSchema.keyof());

const coachPaymentReportTableColumns = (timeZone: string) => [
  columnHelper.accessor((row) => row, {
    id: 'classTime',
    header: 'Class times',

    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const startDateTime = formatInstant(session.startInstant, timeZone);
      const endDateTime = formatInstant(session.endInstant, timeZone);

      const [month, day] = startDateTime.formattedDate.split('/');

      return `${month}/${day}, ${startDateTime.formattedTime} - ${endDateTime.formattedTime}`;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'duration',
    header: 'Class duration',

    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const duration = session.endInstant.since(session.startInstant);

      const minutes = duration.total({ unit: 'minute' });

      return `${minutes} mins (${VideoConferenceTypeLabels[session.videoConferenceType]})`;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'meetingStatus',
    header: 'Class status',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const { sessionCompletionStatus, coachSessionRemarksV2 } = session;

      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={session}
        />
      );
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'feedback',
    header: 'Feedback',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const { hoursCanceledBeforeStart, sessionFeedbacks, lesson } = session;

      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks || []}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
        />
      );
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'pay',
    header: 'Pay',

    cell: ({ getValue }) => {
      const session = getValue();

      return formatCurrency((session?.baseAmount ?? 0) / 100);
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'incentive',
    header: 'Incentive',

    cell: ({ getValue }) => {
      const session = getValue();

      return formatCurrency((session?.incentivesAmount ?? 0) / 100);
    },
  }),
];

function TeachSessionPaymentsDetails() {
  const { payCycleId } = Route.useParams();

  const { timeZone } = useTimeZone();

  const { pageIndex, pageSize, onPaginationChange } = usePaginatedFilters(Route.id, ['pageIndex', 'pageSize']);

  const { data, error, isPending, refetch } = useQuery(
    getCoachSessionPaymentAsCoach({
      filters: {
        pageIndex,
        pageSize,
        filters: {
          payCycleId,
        },
      },
    }),
  );

  useErrorToast(error, 'Error loading payments. Please try again');

  const table = useReactTable({
    data: data?.payments || [],
    columns: coachPaymentReportTableColumns(timeZone),
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    },
  });

  return (
    <InfoPage
      headerText={'Payments'}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Payment',
            link: {
              to: '/payments',
            },
          },
        ],
        currentPage: formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).paymentPlainDate),
      }}
    >
      <InfoCard headerText={'Teaching'}>
        <InfoKeyValue label={'Gross/net earnings'} value={formatCurrency((data?.totalAmountInCents ?? 0) / 100)} />
        <InfoKeyValue
          label={'Period'}
          value={`
            ${formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).startPlainDate)} - ${formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).endPlainDate)}
            `}
        />
        <InfoKeyValue
          label={'Pay initiation date'}
          value={formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).paymentPlainDate)}
        />
        <InfoKeyValue label={'Delivery (1-3 business days)'} value={data?.depositPostedDate} />
      </InfoCard>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        hasError={!!error}
        emptyState="No payments found"
        errorState={<TableErrorState refetchAction={refetch} />}
      />
    </InfoPage>
  );
}
